import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Coins, Users, Building2, ArrowUpRight, ArrowDownRight, Download,
  Plus, Filter, RefreshCw, TrendingUp, Zap, Crown, Rocket, Search,
  Calendar, ChevronDown, Globe, Pencil, Save, X
} from 'lucide-react';
import AutoReloadPanel from '../components/credits/AutoReloadPanel';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Tier Card ───
const TierCard = ({ id, tier, isCurrent }) => {
  const icons = { starter: Zap, growth: Rocket, enterprise: Crown };
  const colors = { starter: 'emerald', growth: 'blue', enterprise: 'amber' };
  const Icon = icons[id] || Zap;
  const color = colors[id] || 'emerald';
  const currency = tier.currency || 'USD';
  const price = tier.monthly_price;

  return (
    <Card className={cn("border-2 transition-all", isCurrent ? `border-${color}-500 shadow-lg` : "border-gray-border hover:border-gray-300")} data-testid={`tier-${id}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", `bg-${color}-100`)}>
            <Icon className={cn("h-5 w-5", `text-${color}-600`)} />
          </div>
          {isCurrent && <Badge className="bg-emerald-brand text-white text-[10px]">Current</Badge>}
        </div>
        <h3 className="text-base font-bold text-text-primary">{tier.name}</h3>
        <p className="text-2xl font-bold text-text-primary mt-1">{tier.monthly_credits.toLocaleString()}<span className="text-xs text-gray-400 font-normal ml-1">credits/mo</span></p>
        {price != null && <p className="text-xs text-gray-500 mt-0.5">{currency} {price.toLocaleString()}/mo</p>}
        <div className="mt-3 space-y-1">
          <p className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Features</p>
          <div className="flex flex-wrap gap-1">
            {tier.features.slice(0, 6).map(f => (
              <span key={f} className="text-[10px] bg-gray-50 text-gray-500 px-1.5 py-0.5 rounded">{f.replace(/_/g, ' ')}</span>
            ))}
            {tier.features.length > 6 && <span className="text-[10px] text-gray-400">+{tier.features.length - 6} more</span>}
          </div>
        </div>
        <div className="mt-2 text-[10px] text-gray-400">
          Team: {tier.limits.team_members === -1 ? 'Unlimited' : tier.limits.team_members} · AI: {tier.limits.ai_generations === -1 ? 'Unlimited' : tier.limits.ai_generations}/mo
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Allocation Dialog ───
const AllocateDialog = ({ open, onClose, onAllocate, targetLabel }) => {
  const [amount, setAmount] = useState('');
  const [tier, setTier] = useState('');
  const [note, setNote] = useState('');

  const handleSubmit = () => {
    const num = parseInt(amount);
    if (!num || num <= 0) { toast.error('Enter a valid amount'); return; }
    onAllocate({ amount: num, tier: tier || undefined, note });
    setAmount(''); setTier(''); setNote('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="text-base">Allocate Credits to {targetLabel}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Credits Amount</Label>
            <Input type="number" placeholder="e.g. 1000" value={amount} onChange={e => setAmount(e.target.value)} className="mt-1" data-testid="allocate-amount" />
          </div>
          <div><Label className="text-xs">Set Tier (optional)</Label>
            <Select value={tier} onValueChange={setTier}>
              <SelectTrigger className="mt-1 text-xs"><SelectValue placeholder="No change" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="starter">Starter</SelectItem>
                <SelectItem value="growth">Growth</SelectItem>
                <SelectItem value="enterprise">Enterprise</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Note (optional)</Label>
            <Input placeholder="e.g. Q2 allocation" value={note} onChange={e => setNote(e.target.value)} className="mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} data-testid="confirm-allocate-btn"><Plus className="h-3.5 w-3.5 mr-1" />Allocate</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Entity Row ───
const EntityRow = ({ entity, type, onAllocate }) => {
  const pct = entity.allocated_credits > 0 ? Math.round((entity.used_credits / entity.allocated_credits) * 100) : 0;
  const tierColors = { starter: 'bg-emerald-100 text-emerald-700', growth: 'bg-blue-100 text-blue-700', enterprise: 'bg-amber-100 text-amber-700' };

  return (
    <div className="flex items-center justify-between p-3 rounded-xl border border-gray-border hover:border-gray-300 transition-colors" data-testid={`entity-${entity.operator_id || entity.subscriber_id}`}>
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className="w-9 h-9 rounded-lg bg-gray-50 flex items-center justify-center shrink-0">
          {type === 'operator' ? <Building2 className="h-4 w-4 text-gray-400" /> : <Users className="h-4 w-4 text-gray-400" />}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-text-primary truncate">{entity.name || entity.email}</p>
          <p className="text-[10px] text-gray-400">{entity.email}{entity.country ? ` · ${entity.country}` : ''}</p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <Badge className={cn("text-[10px]", tierColors[entity.tier] || tierColors.starter)}>{entity.tier}</Badge>
        <div className="text-right">
          <p className="text-sm font-bold text-text-primary">{entity.remaining_credits.toLocaleString()}</p>
          <p className="text-[10px] text-gray-400">{entity.used_credits.toLocaleString()} / {entity.allocated_credits.toLocaleString()} used</p>
        </div>
        <div className="w-16 h-1.5 rounded-full bg-gray-100 overflow-hidden">
          <div className={cn("h-full rounded-full transition-all", pct > 80 ? "bg-red-500" : pct > 50 ? "bg-amber-500" : "bg-emerald-brand")} style={{ width: `${Math.min(pct, 100)}%` }} />
        </div>
        <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => onAllocate(entity)} data-testid={`allocate-btn-${entity.operator_id || entity.subscriber_id}`}>
          <Plus className="h-3 w-3" />Add
        </Button>
      </div>
    </div>
  );
};

// ─── Transaction Row ───
const TransactionRow = ({ tx }) => {
  const isCredit = tx.type === 'allocation' || tx.type === 'recharge';
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0">
      <div className="flex items-center gap-2.5">
        <div className={cn("w-7 h-7 rounded-lg flex items-center justify-center", isCredit ? "bg-emerald-50" : "bg-red-50")}>
          {isCredit ? <ArrowDownRight className="h-3.5 w-3.5 text-emerald-600" /> : <ArrowUpRight className="h-3.5 w-3.5 text-red-500" />}
        </div>
        <div>
          <p className="text-xs font-medium text-text-primary">
            {tx.action_name || tx.type}{tx.note ? ` — ${tx.note}` : ''}
          </p>
          <p className="text-[10px] text-gray-400">
            by {tx.performed_by}
            {tx.performer_role && tx.performer_role !== 'unknown' ? ` (${tx.performer_role.replace('_', ' ')})` : ''}
            {' · '}{new Date(tx.created_at).toLocaleDateString()}
            {' '}{new Date(tx.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
      </div>
      <span className={cn("text-sm font-bold", isCredit ? "text-emerald-600" : "text-red-500")}>
        {isCredit ? '+' : '-'}{tx.amount?.toLocaleString()}
      </span>
    </div>
  );
};

// ─── Main Page ───
export default function CreditManagementPage() {
  const { effectiveRole } = useAuth();
  const [tab, setTab] = useState('overview');
  const [entities, setEntities] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [tiers, setTiers] = useState({});
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allocDialog, setAllocDialog] = useState({ open: false, target: null });
  const [searchQ, setSearchQ] = useState('');
  const [periodFilter, setPeriodFilter] = useState('month');

  // Country tier defaults state
  const [countryDefaults, setCountryDefaults] = useState([]);
  const [globalDefaults, setGlobalDefaults] = useState({});
  const [editingCountry, setEditingCountry] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [newCountry, setNewCountry] = useState('');
  const [savingDefaults, setSavingDefaults] = useState(false);

  const isSuperAdmin = effectiveRole === 'super_admin';
  const isOperator = effectiveRole === 'country_operator';
  const isSubscriber = effectiveRole === 'client_admin';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tiersRes, balRes, txRes] = await Promise.all([
        axios.get(`${API}/api/credit-management/tiers`).catch(() => ({ data: { tiers: {} } })),
        axios.get(`${API}/api/credit-management/balance`).catch(() => ({ data: {} })),
        axios.get(`${API}/api/credit-management/consumption?period=${periodFilter}`).catch(() => ({ data: { transactions: [] } })),
      ]);
      setTiers(tiersRes.data.tiers || {});
      setBalance(balRes.data);
      setTransactions(txRes.data.transactions || []);

      // Fetch managed entities
      if (isSuperAdmin) {
        const opRes = await axios.get(`${API}/api/credit-management/operators`).catch(() => ({ data: { operators: [] } }));
        setEntities(opRes.data.operators || []);
      } else if (isOperator) {
        const subRes = await axios.get(`${API}/api/credit-management/subscribers`).catch(() => ({ data: { subscribers: [] } }));
        setEntities(subRes.data.subscribers || []);
      } else if (isSubscriber) {
        const empRes = await axios.get(`${API}/api/credit-management/employee-usage`).catch(() => ({ data: { by_employee: [] } }));
        setEntities(empRes.data.by_employee || []);
      }
    } catch (err) {
      toast.error('Failed to load credit data');
    }
    setLoading(false);
  }, [isSuperAdmin, isOperator, isSubscriber, periodFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Fetch country tier defaults (for admin/operator)
  const fetchCountryDefaults = useCallback(async () => {
    if (!isSuperAdmin && !isOperator) return;
    try {
      const res = await axios.get(`${API}/api/credit-management/country-defaults`);
      setCountryDefaults(res.data.countries || []);
      setGlobalDefaults(res.data.global_defaults || {});
    } catch { /* */ }
  }, [isSuperAdmin, isOperator]);

  useEffect(() => {
    if (tab === 'tier-defaults') fetchCountryDefaults();
  }, [tab, fetchCountryDefaults]);

  const startEditCountry = (country) => {
    const existing = countryDefaults.find(c => c.country === country);
    if (existing) {
      setEditForm({
        country,
        currency: existing.currency || existing.tiers?.starter?.currency || 'USD',
        timezone: existing.timezone || '',
        language: existing.language || 'en',
        notes: existing.notes || '',
        starter_credits: existing.tiers?.starter?.monthly_credits ?? globalDefaults.starter?.monthly_credits ?? 5000,
        starter_price: existing.tiers?.starter?.monthly_price ?? globalDefaults.starter?.monthly_price ?? 299,
        growth_credits: existing.tiers?.growth?.monthly_credits ?? globalDefaults.growth?.monthly_credits ?? 20000,
        growth_price: existing.tiers?.growth?.monthly_price ?? globalDefaults.growth?.monthly_price ?? 799,
        enterprise_credits: existing.tiers?.enterprise?.monthly_credits ?? globalDefaults.enterprise?.monthly_credits ?? 50000,
        enterprise_price: existing.tiers?.enterprise?.monthly_price ?? globalDefaults.enterprise?.monthly_price ?? 1499,
      });
    } else {
      setEditForm({
        country,
        currency: 'USD', timezone: '', language: 'en', notes: '',
        starter_credits: globalDefaults.starter?.monthly_credits ?? 5000,
        starter_price: globalDefaults.starter?.monthly_price ?? 299,
        growth_credits: globalDefaults.growth?.monthly_credits ?? 20000,
        growth_price: globalDefaults.growth?.monthly_price ?? 799,
        enterprise_credits: globalDefaults.enterprise?.monthly_credits ?? 50000,
        enterprise_price: globalDefaults.enterprise?.monthly_price ?? 1499,
      });
    }
    setEditingCountry(country);
  };

  const saveCountryDefaults = async () => {
    setSavingDefaults(true);
    const country = editForm.country || newCountry;
    if (!country) { toast.error('Country name required'); setSavingDefaults(false); return; }
    try {
      await axios.put(`${API}/api/credit-management/country-defaults/${country.toLowerCase()}`, {
        tiers: {
          starter: { monthly_credits: parseInt(editForm.starter_credits) || 5000, monthly_price: parseInt(editForm.starter_price) || 299, currency: editForm.currency },
          growth: { monthly_credits: parseInt(editForm.growth_credits) || 20000, monthly_price: parseInt(editForm.growth_price) || 799, currency: editForm.currency },
          enterprise: { monthly_credits: parseInt(editForm.enterprise_credits) || 50000, monthly_price: parseInt(editForm.enterprise_price) || 1499, currency: editForm.currency },
        },
        currency: editForm.currency,
        timezone: editForm.timezone,
        language: editForm.language,
        notes: editForm.notes,
      });
      toast.success(`Tier defaults saved for ${country}`);
      setEditingCountry(null);
      setNewCountry('');
      fetchCountryDefaults();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
    setSavingDefaults(false);
  };

  const deleteCountryDefault = async (country) => {
    if (!window.confirm(`Remove tier override for ${country}? Global defaults will apply.`)) return;
    try {
      await axios.delete(`${API}/api/credit-management/country-defaults/${country}`);
      toast.success(`Override removed for ${country}`);
      fetchCountryDefaults();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleAllocate = async ({ amount, tier, note }) => {
    const target = allocDialog.target;
    const endpoint = isSuperAdmin ? 'operators/allocate' : 'subscribers/allocate';
    const payload = {
      [isSuperAdmin ? 'operator_id' : 'subscriber_id']: target.operator_id || target.subscriber_id,
      amount, tier, note,
    };
    try {
      await axios.post(`${API}/api/credit-management/${endpoint}`, payload);
      toast.success(`Allocated ${amount.toLocaleString()} credits`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Allocation failed');
    }
  };

  const handleRecharge = async () => {
    const amount = prompt('Enter recharge amount:');
    if (!amount || isNaN(amount) || parseInt(amount) <= 0) return;
    try {
      await axios.post(`${API}/api/credit-management/recharge`, { amount: parseInt(amount) });
      toast.success(`Recharged ${parseInt(amount).toLocaleString()} credits`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Recharge failed');
    }
  };

  const filteredEntities = entities.filter(e => {
    if (!searchQ) return true;
    const q = searchQ.toLowerCase();
    return (e.name || '').toLowerCase().includes(q) || (e.email || '').toLowerCase().includes(q);
  });

  const entityLabel = isSuperAdmin ? 'Country Operators' : isOperator ? 'Subscribers' : 'Employee Usage';

  return (
    <div className="space-y-4" data-testid="credit-management-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <Coins className="h-5 w-5 text-amber-500" />Credit Management
          </h1>
          <p className="text-xs text-gray-400 mt-0.5">
            {isSuperAdmin ? 'Manage credit allocation to Country Operators' : isOperator ? 'Manage credit allocation to Subscribers' : 'Monitor your team\'s credit usage'}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {(isOperator || isSubscriber) && (
            <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={handleRecharge} data-testid="recharge-btn">
              <Plus className="h-3.5 w-3.5" />Recharge Credits
            </Button>
          )}
          <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => {
            window.open(`${API}/api/credit-management/export?period=${periodFilter}`, '_blank');
          }} data-testid="export-csv-btn">
            <Download className="h-3.5 w-3.5" />Export CSV
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={fetchData} data-testid="refresh-credits-btn">
            <RefreshCw className="h-3.5 w-3.5" />Refresh
          </Button>
        </div>
      </div>

      {/* Tier Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {Object.entries(tiers).map(([id, tier]) => (
          <TierCard key={id} id={id} tier={tier} isCurrent={balance?.tier === id} />
        ))}
      </div>

      {/* Auto-Reload Panel (subscriber/operator only) */}
      {(isSubscriber || isOperator) && <AutoReloadPanel />}

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-gray-50 h-9">
          <TabsTrigger value="overview" className="text-xs">{entityLabel}</TabsTrigger>
          <TabsTrigger value="transactions" className="text-xs">Transactions</TabsTrigger>
          {(isSuperAdmin || isOperator) && (
            <TabsTrigger value="tier-defaults" className="text-xs" data-testid="tier-defaults-tab">
              <Globe className="h-3 w-3 mr-1" />Tier Defaults
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="overview" className="mt-3 space-y-3">
          {/* Search & Filter */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <Input placeholder={`Search ${entityLabel.toLowerCase()}...`} value={searchQ} onChange={e => setSearchQ(e.target.value)}
                className="pl-8 h-8 text-xs" data-testid="search-entities" />
            </div>
          </div>

          {loading ? (
            <div className="text-center py-8 text-gray-400 text-sm">Loading...</div>
          ) : isSubscriber ? (
            /* Employee usage view */
            <Card className="border-gray-border">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Employee Credit Usage</CardTitle></CardHeader>
              <CardContent>
                {filteredEntities.length === 0 ? (
                  <p className="text-xs text-gray-400 py-4 text-center">No usage data yet</p>
                ) : (
                  <div className="space-y-2">
                    {filteredEntities.map((emp, i) => (
                      <div key={i} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center">
                            <Users className="h-3.5 w-3.5 text-blue-500" />
                          </div>
                          <div>
                            <p className="text-xs font-medium">{emp.email}</p>
                            <p className="text-[10px] text-gray-400">{emp.actions} actions</p>
                          </div>
                        </div>
                        <span className="text-sm font-bold text-text-primary">{emp.total_used.toLocaleString()} credits</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            /* Operator/Subscriber list */
            <div className="space-y-2">
              {filteredEntities.length === 0 ? (
                <div className="text-center py-8 text-gray-400 text-sm">No {entityLabel.toLowerCase()} found</div>
              ) : (
                filteredEntities.map((entity, i) => (
                  <EntityRow key={i} entity={entity} type={isSuperAdmin ? 'operator' : 'subscriber'}
                    onAllocate={e => setAllocDialog({ open: true, target: e })} />
                ))
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="transactions" className="mt-3">
          <Card className="border-gray-border">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">Transaction History</CardTitle>
                <Select value={periodFilter} onValueChange={v => { setPeriodFilter(v); }}>
                  <SelectTrigger className="w-28 h-7 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="day">Today</SelectItem>
                    <SelectItem value="week">This Week</SelectItem>
                    <SelectItem value="month">This Month</SelectItem>
                    <SelectItem value="year">This Year</SelectItem>
                    <SelectItem value="all">All Time</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {transactions.length === 0 ? (
                <p className="text-xs text-gray-400 py-4 text-center">No transactions yet</p>
              ) : (
                <div className="max-h-[400px] overflow-y-auto">
                  {transactions.map((tx, i) => <TransactionRow key={i} tx={tx} />)}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tier Defaults Tab */}
        {(isSuperAdmin || isOperator) && (
          <TabsContent value="tier-defaults" className="mt-3 space-y-4" data-testid="tier-defaults-content">
            {/* Global Defaults */}
            <Card className="border-gray-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Globe className="h-4 w-4 text-gray-400" />Global Default Tiers
                  <Badge className="bg-gray-100 text-gray-500 text-[10px]">Fallback</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-3">
                  {Object.entries(globalDefaults).map(([id, tier]) => (
                    <div key={id} className="p-3 rounded-lg bg-gray-50 border border-gray-100">
                      <p className="text-xs font-semibold capitalize">{tier.name || id}</p>
                      <p className="text-lg font-bold text-text-primary">{(tier.monthly_credits || 0).toLocaleString()} <span className="text-[10px] text-gray-400 font-normal">credits</span></p>
                      <p className="text-xs text-gray-500">{tier.currency || 'USD'} {(tier.monthly_price || 0).toLocaleString()}/mo</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Country Overrides */}
            <Card className="border-gray-border">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">Country-Specific Tier Defaults</CardTitle>
                  {isSuperAdmin && !editingCountry && (
                    <div className="flex items-center gap-2">
                      <Input
                        placeholder="Country name..."
                        value={newCountry}
                        onChange={e => setNewCountry(e.target.value)}
                        className="h-7 text-xs w-36"
                        data-testid="new-country-input"
                      />
                      <Button size="sm" className="h-7 text-xs gap-1" disabled={!newCountry.trim()}
                        onClick={() => startEditCountry(newCountry.trim().toLowerCase())}
                        data-testid="add-country-btn"
                      >
                        <Plus className="h-3 w-3" />Add Country
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {/* Editing Form */}
                {editingCountry && (
                  <div className="border border-blue-200 bg-blue-50/50 rounded-xl p-4 mb-4" data-testid="edit-country-form">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold capitalize flex items-center gap-2">
                        <Pencil className="h-3.5 w-3.5 text-blue-500" />
                        Editing: {editingCountry}
                      </h4>
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setEditingCountry(null)}>
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>

                    {/* Country settings row */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                      <div>
                        <Label className="text-[10px] text-gray-500">Currency</Label>
                        <Input value={editForm.currency || ''} onChange={e => setEditForm(f => ({...f, currency: e.target.value}))} className="mt-0.5 h-8 text-xs" data-testid="edit-currency" />
                      </div>
                      <div>
                        <Label className="text-[10px] text-gray-500">Timezone</Label>
                        <Input value={editForm.timezone || ''} onChange={e => setEditForm(f => ({...f, timezone: e.target.value}))} placeholder="e.g. Asia/Manila" className="mt-0.5 h-8 text-xs" />
                      </div>
                      <div>
                        <Label className="text-[10px] text-gray-500">Language</Label>
                        <Input value={editForm.language || ''} onChange={e => setEditForm(f => ({...f, language: e.target.value}))} className="mt-0.5 h-8 text-xs" />
                      </div>
                      <div>
                        <Label className="text-[10px] text-gray-500">Notes</Label>
                        <Input value={editForm.notes || ''} onChange={e => setEditForm(f => ({...f, notes: e.target.value}))} className="mt-0.5 h-8 text-xs" />
                      </div>
                    </div>

                    {/* Tier pricing grid */}
                    <div className="grid grid-cols-3 gap-3">
                      {['starter', 'growth', 'enterprise'].map(tierKey => (
                        <div key={tierKey} className="bg-white rounded-lg p-3 border border-gray-200">
                          <p className="text-xs font-semibold capitalize mb-2">{tierKey}</p>
                          <div className="space-y-2">
                            <div>
                              <Label className="text-[10px] text-gray-500">Monthly Credits</Label>
                              <Input
                                type="number"
                                value={editForm[`${tierKey}_credits`] || ''}
                                onChange={e => setEditForm(f => ({...f, [`${tierKey}_credits`]: e.target.value}))}
                                className="mt-0.5 h-8 text-xs"
                                data-testid={`edit-${tierKey}-credits`}
                              />
                            </div>
                            <div>
                              <Label className="text-[10px] text-gray-500">Price ({editForm.currency || 'USD'})</Label>
                              <Input
                                type="number"
                                value={editForm[`${tierKey}_price`] || ''}
                                onChange={e => setEditForm(f => ({...f, [`${tierKey}_price`]: e.target.value}))}
                                className="mt-0.5 h-8 text-xs"
                                data-testid={`edit-${tierKey}-price`}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="flex justify-end gap-2 mt-3">
                      <Button variant="outline" size="sm" className="text-xs" onClick={() => setEditingCountry(null)}>Cancel</Button>
                      <Button size="sm" className="text-xs gap-1" onClick={saveCountryDefaults} disabled={savingDefaults} data-testid="save-country-btn">
                        <Save className="h-3 w-3" />{savingDefaults ? 'Saving...' : 'Save Defaults'}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Country list */}
                {countryDefaults.length === 0 && !editingCountry ? (
                  <p className="text-xs text-gray-400 py-4 text-center">No country-specific overrides yet. All countries use global defaults.</p>
                ) : (
                  <div className="space-y-2">
                    {countryDefaults.map(cd => (
                      <div key={cd.country} className="flex items-center justify-between p-3 rounded-xl border border-gray-border hover:border-gray-300 transition-colors" data-testid={`country-row-${cd.country}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                            <Globe className="h-4 w-4 text-blue-500" />
                          </div>
                          <div>
                            <p className="text-sm font-medium capitalize">{cd.country}</p>
                            <p className="text-[10px] text-gray-400">
                              {cd.currency || 'USD'} · {cd.timezone || 'Default'} · {cd.notes || 'No notes'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {cd.tiers && Object.entries(cd.tiers).map(([k, v]) => (
                            <div key={k} className="text-right">
                              <p className="text-[10px] text-gray-400 capitalize">{k}</p>
                              <p className="text-xs font-bold">{(v.monthly_credits || '').toLocaleString()}</p>
                            </div>
                          ))}
                          <div className="flex gap-1 ml-2">
                            <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => startEditCountry(cd.country)}>
                              <Pencil className="h-2.5 w-2.5" />Edit
                            </Button>
                            {isSuperAdmin && (
                              <Button size="sm" variant="outline" className="h-7 text-xs text-red-500 hover:text-red-600" onClick={() => deleteCountryDefault(cd.country)}>
                                <X className="h-2.5 w-2.5" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      <AllocateDialog
        open={allocDialog.open}
        onClose={() => setAllocDialog({ open: false, target: null })}
        onAllocate={handleAllocate}
        targetLabel={allocDialog.target?.name || allocDialog.target?.email || ''}
      />
    </div>
  );
}
